// Data-source selection. CRITICAL: fixtures are the DEFAULT — the Supabase
// adapter is chosen ONLY when VITE_SUPABASE_URL is set. This keeps the existing
// tests and the fixtures-backed UI working with no backend wired.

import { getSupabaseClient } from "../lib/supabase";
import { fixturesDataSource } from "./fixturesAdapter";
import { createSupabaseDataSource } from "./supabaseAdapter";
import type { NotifyFamilyInput, PortalDataSource } from "./types";

export type { PortalDataSource, ServerChild } from "./types";
export { fixturesDataSource } from "./fixturesAdapter";
export { createSupabaseDataSource } from "./supabaseAdapter";

/**
 * True only when a Supabase project URL is configured. Pure + injectable so the
 * selection rule is unit-testable without touching the real environment.
 */
export function shouldUseSupabase(
  env: ImportMetaEnv = import.meta.env,
): boolean {
  const url = env.VITE_SUPABASE_URL as string | undefined;
  const key = env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  // Require BOTH. A build that inlined only one of the two VITE_ vars (a common
  // Cloudflare Pages env misconfig — e.g. the key added after the last build)
  // would otherwise pass this gate, try to create the client, and throw at
  // render time, white-screening the whole SPA. With partial/absent config we
  // fall back to the fixtures (demo) adapter instead of crashing.
  return (
    typeof url === "string" &&
    url.trim() !== "" &&
    typeof key === "string" &&
    key.trim() !== ""
  );
}

let _dataSource: PortalDataSource | null = null;

/**
 * Returns the active {@link PortalDataSource} (memoised): the RLS-scoped
 * Supabase adapter when VITE_SUPABASE_URL is set, otherwise the in-memory
 * fixtures adapter (the default).
 */
export function getPortalData(): PortalDataSource {
  if (_dataSource) return _dataSource;
  _dataSource = shouldUseSupabase()
    ? createSupabaseDataSource(getSupabaseClient())
    : fixturesDataSource;
  return _dataSource;
}

/**
 * Fire-and-forget push to the parent's phone (the notify-family edge function).
 *
 * The write it follows has ALREADY succeeded, so nothing here may fail it, delay
 * it or show an error: no await, a synchronous throw is caught too, and a
 * failure is only logged. Push is a courtesy; the suggestion or licence is
 * already in the app the next time it syncs.
 */
export function notifyFamilyInBackground(input: NotifyFamilyInput): void {
  try {
    void getPortalData()
      .notifyFamily(input)
      .catch((err: unknown) => {
        console.warn("[zooylo] notify-family failed (the write itself succeeded)", err);
      });
  } catch (err) {
    console.warn("[zooylo] notify-family failed (the write itself succeeded)", err);
  }
}

/** Resets the memoised data source — for unit tests only. */
export function _resetPortalDataForTest(): void {
  _dataSource = null;
}
